import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend,
    AreaChart, Area
} from 'recharts';
import {
    Database, TrendingUp, Brain, Target, Clock, Loader2, RefreshCw,
    MessageSquare, Search, Zap, CheckCircle, XCircle, Minus,
    Calendar, Activity, Power, Radio, Bell, BarChart2, Cpu
} from 'lucide-react';

const COLORS = ['#00E5FF', '#FF2D95', '#22c55e', '#f59e0b', '#a855f7', '#ef4444', '#06b6d4', '#8b5cf6'];
const SENTIMENT_COLORS = { positive: '#22c55e', negative: '#ef4444', neutral: '#64748b' };

function StatCard({ icon: Icon, label, value, sub, color = 'cyan' }) {
    const colorMap = { cyan: 'text-cyan-400', pink: 'text-pink-400', green: 'text-green-400', amber: 'text-amber-400', purple: 'text-purple-400' };
    return (
        <div className="glass-card p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${colorMap[color] || 'text-white/40'}`} />
                <span className="tech-mono text-[10px] text-white/30">{label}</span>
            </div>
            <div className={`text-2xl font-bold ${colorMap[color]}`}>{value}</div>
            {sub && <div className="text-[10px] text-white/20 mt-1">{sub}</div>}
        </div>
    );
}

function SentimentBadge({ label, score }) {
    if (!label) return <span className="text-[10px] text-white/20">--</span>;
    const c = SENTIMENT_COLORS[label] || '#64748b';
    return (
        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${c}20`, color: c, border: `1px solid ${c}33` }}>
            {label === 'positive' ? '+' : label === 'negative' ? '-' : '~'}{score !== null ? ` ${score.toFixed(2)}` : ''}
        </span>
    );
}

export default function IntelligencePipeline() {
    const [tab, setTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [trends, setTrends] = useState(null);
    const [accuracy, setAccuracy] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [items, setItems] = useState([]);
    const [autoScraper, setAutoScraper] = useState(null);
    const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
    const [retrain, setRetrain] = useState(null);
    const [showNotifs, setShowNotifs] = useState(false);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, trendsRes, accuracyRes, scheduleRes, itemsRes, autoRes, notifsRes, retrainRes] = await Promise.all([
                API.get('/intelligence/pipeline/stats').catch(() => ({ data: null })),
                API.get('/intelligence/trends').catch(() => ({ data: null })),
                API.get('/intelligence/accuracy').catch(() => ({ data: null })),
                API.get('/intelligence/schedule').catch(() => ({ data: null })),
                API.get('/intelligence/pipeline/items').catch(() => ({ data: { items: [] } })),
                API.get('/intelligence/auto-scraper/status').catch(() => ({ data: null })),
                API.get('/intelligence/notifications').catch(() => ({ data: { notifications: [], unread_count: 0 } })),
                API.get('/intelligence/retrain/dashboard').catch(() => ({ data: null })),
            ]);
            setStats(statsRes.data);
            setTrends(trendsRes.data);
            setAccuracy(accuracyRes.data);
            setSchedule(scheduleRes.data);
            setItems(itemsRes.data?.items || []);
            setAutoScraper(autoRes.data);
            setNotifications(notifsRes.data || { notifications: [], unread_count: 0 });
            setRetrain(retrainRes.data);
        } catch (e) { console.error(e); }
        setLoading(false);
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const handleAction = async (action, endpoint, method = 'post', body = {}) => {
        setActionLoading(action);
        try {
            if (method === 'post') await API.post(endpoint, body);
            else await API.get(endpoint);
            await loadData();
        } catch (e) { console.error(e); }
        setActionLoading('');
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: Database },
        { id: 'auto', label: 'Auto-Scraper', icon: Radio },
        { id: 'retrain', label: 'Model Retrain', icon: Cpu },
        { id: 'trends', label: 'Google Trends', icon: TrendingUp },
        { id: 'sentiment', label: 'Sentiment', icon: Brain },
        { id: 'accuracy', label: 'Accuracy', icon: Target },
        { id: 'schedule', label: 'Smart Schedule', icon: Clock },
        { id: 'items', label: 'Raw Data', icon: MessageSquare },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="pipeline-loading">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
        );
    }

    const sourceChartData = stats?.by_source?.map(s => ({
        name: s.source.replace('_', '/'),
        count: s.count,
        sentiment: s.avg_sentiment ? parseFloat((s.avg_sentiment * 100).toFixed(1)) : 0,
    })) || [];

    const sentimentPieData = stats?.sentiment_distribution
        ? Object.entries(stats.sentiment_distribution).map(([k, v]) => ({ name: k, value: v }))
        : [];

    const trendsBarData = trends?.team_trends?.slice(0, 10).map(t => ({
        name: t.term,
        interest: t.avg_interest,
        max: t.max_interest,
    })) || [];

    const priorityColors = { live: '#ef4444', high: '#f59e0b', medium: '#22c55e', low: '#64748b', idle: '#334155' };

    return (
        <div className="space-y-6" data-testid="intelligence-pipeline-page">
            {/* Header */}
            <div className="glass-card p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <Database className="w-5 h-5 text-cyan-400" />
                        <h2 className="heading-primary text-base md:text-xl text-white">INTELLIGENCE PIPELINE</h2>
                        <div className="badge-ai">DATA ENGINE</div>
                    </div>
                    <Button onClick={() => loadData()} size="sm" variant="outline" className="border-white/10 text-white/60 hover:text-white text-xs" data-testid="refresh-pipeline">
                        <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                    </Button>
                    {/* Notification Bell */}
                    <div className="relative">
                        <button onClick={() => setShowNotifs(!showNotifs)} className="relative p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all" data-testid="notification-bell">
                            <Bell className={`w-4 h-4 ${notifications.unread_count > 0 ? 'text-amber-400' : 'text-white/40'}`} />
                            {notifications.unread_count > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">
                                    {notifications.unread_count}
                                </span>
                            )}
                        </button>
                        {showNotifs && (
                            <div className="absolute right-0 top-10 w-80 max-h-80 overflow-y-auto bg-[#0a0a0a] border border-white/10 rounded-lg shadow-2xl z-50" data-testid="notif-panel">
                                <div className="flex items-center justify-between p-3 border-b border-white/10">
                                    <span className="text-xs text-white/60 font-semibold">Notifications</span>
                                    {notifications.unread_count > 0 && (
                                        <button onClick={async () => { await API.post('/intelligence/notifications/mark-read'); await loadData(); setShowNotifs(false); }}
                                            className="text-[10px] text-cyan-400 hover:text-cyan-300">Mark all read</button>
                                    )}
                                </div>
                                {notifications.notifications?.length > 0 ? notifications.notifications.map((n, i) => {
                                    const typeIcon = { match_complete: '🏏', webhook_data: '📡', error: '⚠️', info: 'ℹ️' };
                                    return (
                                        <div key={i} className={`p-2.5 border-b border-white/5 ${!n.read ? 'bg-white/5' : ''}`}>
                                            <div className="flex items-start gap-2">
                                                <span className="text-xs">{typeIcon[n.type] || '📢'}</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs text-white/80 font-medium">{n.title}</div>
                                                    <div className="text-[10px] text-white/40 mt-0.5 line-clamp-2">{n.message}</div>
                                                    <div className="text-[9px] text-white/20 mt-0.5">{n.created_at?.slice(0, 16)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div className="p-4 text-center text-xs text-white/20">No notifications yet</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 overflow-x-auto pb-1">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-all ${tab === t.id ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-white/5 text-white/40 hover:text-white/60 border border-transparent'}`}>
                        <t.icon className="w-3.5 h-3.5" />
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ─── OVERVIEW TAB ─────────────────────────────────────────── */}
            {tab === 'overview' && (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <StatCard icon={Database} label="Total Items" value={stats?.total_items || 0} sub="stored in pipeline" color="cyan" />
                        <StatCard icon={Brain} label="Scored" value={stats?.scored_items || 0} sub={`${stats?.unscored_items || 0} unscored`} color="green" />
                        <StatCard icon={TrendingUp} label="Trends" value={stats?.trends_items || 0} sub="Google Trends" color="amber" />
                        <StatCard icon={MessageSquare} label="Reddit" value={stats?.reddit_items || 0} sub="posts captured" color="purple" />
                        <StatCard icon={Target} label="Accuracy" value={accuracy?.accuracy_pct ? `${accuracy.accuracy_pct}%` : 'N/A'} sub={`${accuracy?.total_matches || 0} matches`} color="pink" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Source Distribution */}
                        <div className="glass-card p-5" data-testid="source-distribution-chart">
                            <h3 className="text-xs text-white/40 tech-mono mb-3">ITEMS BY SOURCE</h3>
                            {sourceChartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={sourceChartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                                        <XAxis dataKey="name" tick={{ fill: '#666', fontSize: 9 }} />
                                        <YAxis tick={{ fill: '#555', fontSize: 9 }} />
                                        <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8 }} />
                                        <Bar dataKey="count" fill="#00E5FF" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-[220px] flex items-center justify-center text-white/20 text-xs">No data yet — trigger a scrape</div>
                            )}
                        </div>

                        {/* Sentiment Pie */}
                        <div className="glass-card p-5" data-testid="sentiment-pie-chart">
                            <h3 className="text-xs text-white/40 tech-mono mb-3">SENTIMENT DISTRIBUTION</h3>
                            {sentimentPieData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie data={sentimentPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                            {sentimentPieData.map((entry, i) => (
                                                <Cell key={i} fill={SENTIMENT_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-[220px] flex items-center justify-center text-white/20 text-xs">Score sentiment to see distribution</div>
                            )}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="glass-card p-5">
                        <h3 className="text-xs text-white/40 tech-mono mb-3">QUICK ACTIONS</h3>
                        <div className="flex flex-wrap gap-2">
                            <Button onClick={() => handleAction('scrape', '/intelligence/scrape', 'post', {})} disabled={!!actionLoading} size="sm" className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs" data-testid="btn-full-scrape">
                                {actionLoading === 'scrape' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />} Full Scrape (All Sources)
                            </Button>
                            <Button onClick={() => handleAction('trends', '/intelligence/trends/scrape')} disabled={!!actionLoading} size="sm" className="bg-amber-600 hover:bg-amber-500 text-white text-xs" data-testid="btn-trends-scrape">
                                {actionLoading === 'trends' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <TrendingUp className="w-3 h-3 mr-1" />} Google Trends
                            </Button>
                            <Button onClick={() => handleAction('reddit', '/intelligence/reddit/scrape', 'post', {})} disabled={!!actionLoading} size="sm" className="bg-purple-600 hover:bg-purple-500 text-white text-xs" data-testid="btn-reddit-scrape">
                                {actionLoading === 'reddit' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MessageSquare className="w-3 h-3 mr-1" />} Reddit Scrape
                            </Button>
                            <Button onClick={() => handleAction('sentiment', '/intelligence/pipeline/score-sentiment')} disabled={!!actionLoading} size="sm" className="bg-green-600 hover:bg-green-500 text-white text-xs" data-testid="btn-score-sentiment">
                                {actionLoading === 'sentiment' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Brain className="w-3 h-3 mr-1" />} Score Sentiment
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── AUTO-SCRAPER TAB ──────────────────────────────────── */}
            {tab === 'auto' && (
                <div className="space-y-4">
                    {/* Status Banner */}
                    <div className={`glass-card p-5 border-l-4 ${autoScraper?.config?.auto_scrape ? 'border-l-green-500' : 'border-l-red-500'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <Radio className={`w-5 h-5 ${autoScraper?.config?.auto_scrape ? 'text-green-400 animate-pulse' : 'text-red-400'}`} />
                                <h3 className="text-sm text-white font-semibold">AUTONOMOUS SCRAPING ENGINE</h3>
                                <span className={`text-[10px] px-2 py-0.5 rounded tech-mono ${autoScraper?.config?.auto_scrape ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                                    {autoScraper?.config?.auto_scrape ? 'ACTIVE' : 'DISABLED'}
                                </span>
                            </div>
                            <Button
                                onClick={async () => {
                                    const newState = !autoScraper?.config?.auto_scrape;
                                    setActionLoading('toggle');
                                    try {
                                        await API.put('/intelligence/auto-scraper/toggle', { auto_scrape: newState, match_day_boost: true });
                                        await loadData();
                                    } catch (e) { console.error(e); }
                                    setActionLoading('');
                                }}
                                disabled={!!actionLoading}
                                size="sm"
                                className={`text-xs text-white ${autoScraper?.config?.auto_scrape ? 'bg-red-600 hover:bg-red-500' : 'bg-green-600 hover:bg-green-500'}`}
                                data-testid="btn-toggle-auto-scraper"
                            >
                                {actionLoading === 'toggle' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Power className="w-3 h-3 mr-1" />}
                                {autoScraper?.config?.auto_scrape ? 'Disable' : 'Enable'} Auto-Scraper
                            </Button>
                        </div>

                        {/* State Info */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <div className="bg-white/5 rounded-lg p-3">
                                <div className="text-[10px] text-white/30 tech-mono">STATUS</div>
                                <div className={`text-sm font-bold ${autoScraper?.state?.status === 'idle' ? 'text-green-400' : autoScraper?.state?.status === 'disabled' ? 'text-red-400' : autoScraper?.state?.status?.startsWith('live') ? 'text-pink-400' : 'text-amber-400'}`}>
                                    {(autoScraper?.state?.status || 'unknown').toUpperCase().replace(/_/g, ' ')}
                                </div>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3">
                                <div className="text-[10px] text-white/30 tech-mono">TRENDS SCRAPES</div>
                                <div className="text-sm font-bold text-amber-400">{autoScraper?.state?.total_trends_scrapes || 0}</div>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3">
                                <div className="text-[10px] text-white/30 tech-mono">FULL SCRAPES</div>
                                <div className="text-sm font-bold text-cyan-400">{autoScraper?.state?.total_auto_scrapes || 0}</div>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3">
                                <div className="text-[10px] text-white/30 tech-mono">NEXT ACTION</div>
                                <div className="text-xs text-white/60 truncate">{autoScraper?.state?.next_action || 'Waiting...'}</div>
                            </div>
                        </div>

                        {/* Live Match + Retrain Row */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className={`rounded-lg p-3 border ${autoScraper?.state?.live_match_active ? 'bg-pink-500/10 border-pink-500/30' : 'bg-white/5 border-white/10'}`}>
                                <div className="text-[10px] text-white/30 tech-mono">LIVE SCRAPES</div>
                                <div className={`text-sm font-bold ${autoScraper?.state?.live_match_active ? 'text-pink-400' : 'text-white/40'}`}>
                                    {autoScraper?.state?.total_live_scrapes || 0}
                                    {autoScraper?.state?.live_match_active && <span className="ml-1 text-[9px] animate-pulse">LIVE</span>}
                                </div>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                                <div className="text-[10px] text-white/30 tech-mono">AUTO-RETRAINS</div>
                                <div className="text-sm font-bold text-green-400">{autoScraper?.state?.total_retrain_events || 0}</div>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                                <div className="text-[10px] text-white/30 tech-mono">LIVE MATCH ID</div>
                                <div className="text-xs text-white/40 truncate">{autoScraper?.state?.live_match_id || '—'}</div>
                            </div>
                        </div>

                        {/* Timestamps */}
                        <div className="flex flex-wrap gap-4 text-[10px] text-white/30 tech-mono">
                            {autoScraper?.state?.last_trends_scrape && (
                                <span>Last Trends: {new Date(autoScraper.state.last_trends_scrape).toLocaleString()}</span>
                            )}
                            {autoScraper?.state?.last_full_scrape && (
                                <span>Last Full Scrape: {new Date(autoScraper.state.last_full_scrape).toLocaleString()}</span>
                            )}
                        </div>
                    </div>

                    {/* How It Works */}
                    <div className="glass-card p-5">
                        <h4 className="text-xs text-white/40 tech-mono mb-3">HOW AUTO-SCRAPER WORKS</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <TrendingUp className="w-4 h-4 text-amber-400" />
                                    <span className="text-xs text-white font-semibold">Google Trends — Every 2 Hours</span>
                                </div>
                                <div className="text-[10px] text-white/40 space-y-1">
                                    <div>Tracks search interest for all IPL teams + "IPL 2026"</div>
                                    <div>Costs ~0.01 CU per run (~$0.003)</div>
                                    <div>Detects momentum shifts and public confidence swings</div>
                                </div>
                            </div>
                            <div className="bg-cyan-500/5 border border-cyan-500/10 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Zap className="w-4 h-4 text-cyan-400" />
                                    <span className="text-xs text-white font-semibold">Full Scrape — 30min Before Match</span>
                                </div>
                                <div className="text-[10px] text-white/40 space-y-1">
                                    <div>Twitter/X, Reddit, YouTube, Instagram, News, Trends</div>
                                    <div>Auto-stores + sentiment scores via GPT-5.2</div>
                                    <div>Pre-match snapshot for accuracy tracking</div>
                                </div>
                            </div>
                            <div className="bg-pink-500/5 border border-pink-500/10 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Radio className="w-4 h-4 text-pink-400" />
                                    <span className="text-xs text-white font-semibold">Live Mode — Every 15min</span>
                                </div>
                                <div className="text-[10px] text-white/40 space-y-1">
                                    <div>Twitter/X + Reddit + News during live match</div>
                                    <div>Auto-retrain + Elo update on match completion</div>
                                    <div>Sentiment accuracy feedback loop closes</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Activity Log */}
                    <div className="glass-card p-5" data-testid="auto-scraper-logs">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-xs text-white/40 tech-mono">ACTIVITY LOG</h4>
                            <Button onClick={() => loadData()} size="sm" variant="ghost" className="text-white/30 hover:text-white text-[10px] h-6 px-2">
                                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                            </Button>
                        </div>

                        {autoScraper?.logs?.length > 0 ? (
                            <div className="space-y-1 max-h-[400px] overflow-y-auto">
                                {autoScraper.logs.map((log, i) => {
                                    const typeColors = {
                                        trends_auto: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
                                        pre_match_full: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
                                        pre_match_complete: 'text-green-400 bg-green-500/10 border-green-500/20',
                                        sentiment_snapshot: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
                                        config_change: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
                                        error: 'text-red-400 bg-red-500/10 border-red-500/20',
                                        trends_error: 'text-red-400 bg-red-500/10 border-red-500/20',
                                        system: 'text-white/40 bg-white/5 border-white/10',
                                    };
                                    const colors = typeColors[log.type] || typeColors.system;
                                    return (
                                        <div key={i} className={`flex items-start gap-2 rounded-lg p-2 border ${colors}`}>
                                            <span className="text-[9px] tech-mono shrink-0 w-32 opacity-60">{log.timestamp?.slice(11, 19) || ''} {log.timestamp?.slice(0, 10)}</span>
                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-black/20 tech-mono shrink-0">{log.type}</span>
                                            <span className="text-xs opacity-80 flex-1">{log.details}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-white/20">
                                <Radio className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                <div className="text-xs">No activity yet</div>
                                <div className="text-[10px] mt-1">Enable the auto-scraper to start collecting intelligence automatically</div>
                            </div>
                        )}
                    </div>

                    {/* Errors */}
                    {autoScraper?.state?.errors?.length > 0 && (
                        <div className="glass-card p-5 border-l-4 border-l-red-500">
                            <h4 className="text-xs text-red-400 tech-mono mb-2">RECENT ERRORS</h4>
                            <div className="space-y-1">
                                {autoScraper.state.errors.map((err, i) => (
                                    <div key={i} className="text-[10px] text-red-400/60 bg-red-500/5 rounded p-1.5">
                                        <span className="text-white/20">{err.time?.slice(11, 19)}</span> {err.error}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ─── MODEL RETRAIN TAB (P3) ──────────────────────────────── */}
            {tab === 'retrain' && (
                <div className="space-y-4">
                    {/* Accuracy Overview */}
                    <div className="glass-card p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Cpu className="w-4 h-4 text-cyan-400" />
                                <h3 className="text-sm text-white font-semibold">MODEL RETRAINING ENGINE</h3>
                                <span className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 tech-mono">AUTONOMOUS</span>
                            </div>
                            <div className="flex gap-2">
                                <Button onClick={() => handleAction('batch_retrain', '/intelligence/retrain/batch')} disabled={!!actionLoading} size="sm" className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs" data-testid="btn-batch-retrain">
                                    {actionLoading === 'batch_retrain' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Cpu className="w-3 h-3 mr-1" />} Batch Retrain All
                                </Button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div className="bg-white/5 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-cyan-400">{retrain?.accuracy_trend?.current_accuracy || 0}%</div>
                                <div className="text-[10px] text-white/30 tech-mono">MODEL ACCURACY</div>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-white">{retrain?.matches_analyzed || 0}</div>
                                <div className="text-[10px] text-white/30 tech-mono">MATCHES ANALYZED</div>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-amber-400">{retrain?.ball_by_ball_records || 0}</div>
                                <div className="text-[10px] text-white/30 tech-mono">BALL-BY-BALL RECORDS</div>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-green-400">{retrain?.auto_retrain_count || 0}</div>
                                <div className="text-[10px] text-white/30 tech-mono">AUTO-RETRAINS</div>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-purple-400">{retrain?.elo_ratings?.length || 0}</div>
                                <div className="text-[10px] text-white/30 tech-mono">ELO TRACKED TEAMS</div>
                            </div>
                        </div>
                    </div>

                    {/* Accuracy Trend Chart */}
                    {retrain?.accuracy_trend?.trend?.length > 0 && (
                        <div className="glass-card p-5" data-testid="accuracy-trend-chart">
                            <h3 className="text-xs text-white/40 tech-mono mb-3">ACCURACY TREND OVER TIME</h3>
                            <ResponsiveContainer width="100%" height={220}>
                                <LineChart data={retrain.accuracy_trend.trend}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                                    <XAxis dataKey="match_id" tick={{ fill: '#555', fontSize: 8 }} />
                                    <YAxis domain={[0, 100]} tick={{ fill: '#555', fontSize: 9 }} />
                                    <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8 }} />
                                    <Line type="monotone" dataKey="accuracy" stroke="#00E5FF" strokeWidth={2} dot={{ fill: '#00E5FF', r: 3 }} name="Accuracy %" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Elo Ratings */}
                        <div className="glass-card p-5" data-testid="elo-ratings-table">
                            <h3 className="text-xs text-white/40 tech-mono mb-3">ELO RATINGS</h3>
                            {retrain?.elo_ratings?.length > 0 ? (
                                <div className="space-y-1">
                                    {retrain.elo_ratings.map((elo, i) => (
                                        <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg p-2">
                                            <span className="text-[10px] text-white/20 w-4 text-right">#{i + 1}</span>
                                            <span className="text-xs text-white/70 flex-1">{elo.team_id}</span>
                                            <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                                                <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full" style={{ width: `${((elo.rating - 1400) / 200) * 100}%` }} />
                                            </div>
                                            <span className="text-xs font-mono text-cyan-400 w-12 text-right">{Math.round(elo.rating)}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-white/20 text-xs">No Elo data yet — ratings update after match completions</div>
                            )}
                        </div>

                        {/* Recent Retrains */}
                        <div className="glass-card p-5" data-testid="recent-retrains">
                            <h3 className="text-xs text-white/40 tech-mono mb-3">RECENT RETRAINING EVENTS</h3>
                            {retrain?.recent_retrains?.length > 0 ? (
                                <div className="space-y-1">
                                    {retrain.recent_retrains.map((r, i) => (
                                        <div key={i} className="bg-white/5 rounded-lg p-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-white/60">{r.match_id}</span>
                                                <span className="text-xs text-green-400 font-mono">{r.accuracy_after_match}%</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-[10px] text-white/20 mt-1">
                                                <span>Correct: {r.correct_predictions}/{r.total_predictions}</span>
                                                <span>{r.timestamp?.slice(0, 16)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-white/20 text-xs">No retrains yet — happens automatically after each match</div>
                            )}
                        </div>
                    </div>

                    {/* How P3 Works */}
                    <div className="glass-card p-5">
                        <h4 className="text-xs text-white/40 tech-mono mb-2">HOW AUTONOMOUS RETRAINING WORKS</h4>
                        <div className="space-y-2 text-xs text-white/50">
                            <div className="flex items-start gap-2"><span className="text-cyan-400 font-bold">1.</span> During live match: Ball-by-ball data captured every 20 seconds via Sportmonks</div>
                            <div className="flex items-start gap-2"><span className="text-cyan-400 font-bold">2.</span> Over-by-over: Win probability recalculated using run rate deviation model</div>
                            <div className="flex items-start gap-2"><span className="text-cyan-400 font-bold">3.</span> Impact player substitution detected → win% shift applied instantly</div>
                            <div className="flex items-start gap-2"><span className="text-cyan-400 font-bold">4.</span> Match completes → Elo ratings updated → Prediction error analyzed</div>
                            <div className="flex items-start gap-2"><span className="text-cyan-400 font-bold">5.</span> Sentiment accuracy recorded → Engine weights adjusted based on confidence vs correctness</div>
                            <div className="flex items-start gap-2"><span className="text-cyan-400 font-bold">6.</span> Model improves after every single match — zero manual intervention</div>
                        </div>
                    </div>

                    {/* Prediction Analyses */}
                    {retrain?.prediction_analyses?.length > 0 && (
                        <div className="glass-card p-5" data-testid="prediction-analyses">
                            <h3 className="text-xs text-white/40 tech-mono mb-3">PREDICTION ANALYSES</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="border-b border-white/10">
                                            <th className="text-left py-2 px-2 text-white/30">MATCH</th>
                                            <th className="text-center py-2 px-2 text-white/30">PREDICTED</th>
                                            <th className="text-center py-2 px-2 text-white/30">ACTUAL</th>
                                            <th className="text-center py-2 px-2 text-white/30">CORRECT</th>
                                            <th className="text-right py-2 px-2 text-white/30">ERROR</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {retrain.prediction_analyses.map((a, i) => (
                                            <tr key={i} className="border-b border-white/5">
                                                <td className="py-2 px-2 text-white/50">{a.home_team} vs {a.away_team}</td>
                                                <td className="py-2 px-2 text-center text-white/40">{a.predicted_winner}</td>
                                                <td className="py-2 px-2 text-center text-white/60">{a.actual_winner}</td>
                                                <td className="py-2 px-2 text-center">
                                                    {a.correct ? <CheckCircle className="w-3.5 h-3.5 text-green-400 mx-auto" /> : <XCircle className="w-3.5 h-3.5 text-red-400 mx-auto" />}
                                                </td>
                                                <td className="py-2 px-2 text-right text-amber-400">{a.prediction_error}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ─── GOOGLE TRENDS TAB ───────────────────────────────────── */}
            {tab === 'trends' && (
                <div className="space-y-4">
                    <div className="glass-card p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-amber-400" />
                                <h3 className="text-sm text-white font-semibold">GOOGLE TRENDS — IPL TEAM POPULARITY</h3>
                            </div>
                            <Button onClick={() => handleAction('trends', '/intelligence/trends/scrape')} disabled={!!actionLoading} size="sm" className="bg-amber-600 hover:bg-amber-500 text-white text-xs" data-testid="btn-trends-refresh">
                                {actionLoading === 'trends' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Fetch Latest
                            </Button>
                        </div>

                        {trendsBarData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={trendsBarData} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                                    <XAxis type="number" tick={{ fill: '#555', fontSize: 9 }} />
                                    <YAxis dataKey="name" type="category" width={60} tick={{ fill: '#aaa', fontSize: 10 }} />
                                    <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8 }} />
                                    <Bar dataKey="interest" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Avg Interest" />
                                    <Bar dataKey="max" fill="#f59e0b33" radius={[0, 4, 4, 0]} name="Peak Interest" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-[300px] flex flex-col items-center justify-center text-white/20">
                                <TrendingUp className="w-8 h-8 mb-2 opacity-30" />
                                <div className="text-xs">No trends data yet</div>
                                <div className="text-[10px] mt-1">Click "Fetch Latest" to scrape Google Trends</div>
                            </div>
                        )}
                    </div>

                    {/* Trends Table */}
                    {trends?.team_trends?.length > 0 && (
                        <div className="glass-card p-5" data-testid="trends-table">
                            <h3 className="text-xs text-white/40 tech-mono mb-3">TREND DETAILS</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="border-b border-white/10">
                                            <th className="text-left py-2 px-2 text-white/30 tech-mono">TERM</th>
                                            <th className="text-right py-2 px-2 text-white/30 tech-mono">AVG</th>
                                            <th className="text-right py-2 px-2 text-white/30 tech-mono">PEAK</th>
                                            <th className="text-right py-2 px-2 text-white/30 tech-mono">POINTS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trends.team_trends.map((t, i) => (
                                            <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                                                <td className="py-2 px-2 text-white">{t.term}</td>
                                                <td className="py-2 px-2 text-right text-amber-400">{t.avg_interest}</td>
                                                <td className="py-2 px-2 text-right text-amber-300">{t.max_interest}</td>
                                                <td className="py-2 px-2 text-right text-white/40">{t.data_points}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ─── SENTIMENT TAB ───────────────────────────────────────── */}
            {tab === 'sentiment' && (
                <div className="space-y-4">
                    <div className="glass-card p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Brain className="w-4 h-4 text-green-400" />
                                <h3 className="text-sm text-white font-semibold">SENTIMENT ANALYSIS ENGINE</h3>
                            </div>
                            <Button onClick={() => handleAction('sentiment', '/intelligence/pipeline/score-sentiment')} disabled={!!actionLoading} size="sm" className="bg-green-600 hover:bg-green-500 text-white text-xs" data-testid="btn-sentiment-score">
                                {actionLoading === 'sentiment' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Brain className="w-3 h-3 mr-1" />} Score Unscored Items
                            </Button>
                        </div>

                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
                                <div className="text-lg font-bold text-green-400">{stats?.sentiment_distribution?.positive || 0}</div>
                                <div className="text-[10px] text-green-400/60 tech-mono">POSITIVE</div>
                            </div>
                            <div className="bg-slate-500/10 border border-slate-500/20 rounded-lg p-3 text-center">
                                <div className="text-lg font-bold text-slate-400">{stats?.sentiment_distribution?.neutral || 0}</div>
                                <div className="text-[10px] text-slate-400/60 tech-mono">NEUTRAL</div>
                            </div>
                            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
                                <div className="text-lg font-bold text-red-400">{stats?.sentiment_distribution?.negative || 0}</div>
                                <div className="text-[10px] text-red-400/60 tech-mono">NEGATIVE</div>
                            </div>
                        </div>

                        {/* Sentiment by Source */}
                        {stats?.by_source?.length > 0 && (
                            <div>
                                <h4 className="text-xs text-white/30 tech-mono mb-2">SENTIMENT BY SOURCE</h4>
                                <div className="space-y-2">
                                    {stats.by_source.map((s, i) => (
                                        <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg p-2">
                                            <span className="text-xs text-white/60 w-24">{s.source.replace('_', '/')}</span>
                                            <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                                                <div className="h-full rounded-full transition-all" style={{
                                                    width: `${Math.abs((s.avg_sentiment || 0) * 100)}%`,
                                                    backgroundColor: (s.avg_sentiment || 0) > 0 ? '#22c55e' : (s.avg_sentiment || 0) < 0 ? '#ef4444' : '#64748b'
                                                }} />
                                            </div>
                                            <span className={`text-xs font-mono w-16 text-right ${(s.avg_sentiment || 0) > 0 ? 'text-green-400' : (s.avg_sentiment || 0) < 0 ? 'text-red-400' : 'text-white/30'}`}>
                                                {s.avg_sentiment !== null ? (s.avg_sentiment > 0 ? '+' : '') + s.avg_sentiment.toFixed(3) : '--'}
                                            </span>
                                            <span className="text-[10px] text-white/20 w-12 text-right">{s.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ─── ACCURACY TAB ────────────────────────────────────────── */}
            {tab === 'accuracy' && (
                <div className="space-y-4">
                    <div className="glass-card p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Target className="w-4 h-4 text-pink-400" />
                            <h3 className="text-sm text-white font-semibold">PREDICTION ACCURACY FEEDBACK LOOP</h3>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <div className="bg-white/5 rounded-lg p-3 text-center">
                                <div className="text-2xl font-bold text-white">{accuracy?.total_matches || 0}</div>
                                <div className="text-[10px] text-white/30 tech-mono">MATCHES TRACKED</div>
                            </div>
                            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
                                <div className="text-2xl font-bold text-green-400">{accuracy?.correct_predictions || 0}</div>
                                <div className="text-[10px] text-green-400/60 tech-mono">CORRECT</div>
                            </div>
                            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
                                <div className="text-2xl font-bold text-red-400">{accuracy?.incorrect_predictions || 0}</div>
                                <div className="text-[10px] text-red-400/60 tech-mono">INCORRECT</div>
                            </div>
                            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3 text-center">
                                <div className="text-2xl font-bold text-cyan-400">{accuracy?.accuracy_pct || 0}%</div>
                                <div className="text-[10px] text-cyan-400/60 tech-mono">ACCURACY</div>
                            </div>
                        </div>

                        {accuracy?.recent_results?.length > 0 ? (
                            <div>
                                <h4 className="text-xs text-white/30 tech-mono mb-2">RECENT RESULTS</h4>
                                <div className="space-y-1">
                                    {accuracy.recent_results.map((r, i) => (
                                        <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg p-2">
                                            {r.sentiment_predicted_correctly === true ? <CheckCircle className="w-4 h-4 text-green-400 shrink-0" /> :
                                             r.sentiment_predicted_correctly === false ? <XCircle className="w-4 h-4 text-red-400 shrink-0" /> :
                                             <Minus className="w-4 h-4 text-white/20 shrink-0" />}
                                            <span className="text-xs text-white/60 flex-1">{r.match_id}</span>
                                            <span className="text-xs text-white/40">Winner: {r.winner}</span>
                                            <span className="text-[10px] text-white/20">{r.recorded_at?.slice(0, 10)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-white/20">
                                <Target className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                <div className="text-xs">No accuracy data yet</div>
                                <div className="text-[10px] mt-1">Accuracy tracking starts once matches are completed and outcomes recorded</div>
                            </div>
                        )}
                    </div>

                    <div className="glass-card p-5">
                        <h4 className="text-xs text-white/40 tech-mono mb-2">HOW IT WORKS</h4>
                        <div className="space-y-2 text-xs text-white/50">
                            <div className="flex items-start gap-2"><span className="text-cyan-400 font-bold">1.</span> Pre-match: Scrape intelligence → Store items → Score sentiment</div>
                            <div className="flex items-start gap-2"><span className="text-cyan-400 font-bold">2.</span> Take pre-match sentiment snapshot (avg positive/negative per source)</div>
                            <div className="flex items-start gap-2"><span className="text-cyan-400 font-bold">3.</span> After match: Record actual winner → Compare with sentiment prediction</div>
                            <div className="flex items-start gap-2"><span className="text-cyan-400 font-bold">4.</span> Model learns which sources & sentiment thresholds predict most accurately</div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── SMART SCHEDULE TAB ──────────────────────────────────── */}
            {tab === 'schedule' && (
                <div className="space-y-4">
                    <div className="glass-card p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-cyan-400" />
                                <h3 className="text-sm text-white font-semibold">SMART SCRAPING SCHEDULE</h3>
                            </div>
                        </div>

                        <div className="bg-white/5 rounded-lg p-3 mb-4 text-xs text-white/50">
                            <div className="flex items-center gap-2 mb-1"><Zap className="w-3 h-3 text-amber-400" /> <span className="text-white/70 font-semibold">Budget Optimizer</span></div>
                            Scraping frequency auto-adjusts based on match proximity. Saves credits by focusing on match days and using cheap sources (Google Trends) during idle periods.
                        </div>

                        {schedule?.schedule?.length > 0 ? (
                            <div className="space-y-2">
                                {schedule.schedule.map((s, i) => (
                                    <div key={i} className="bg-white/5 rounded-lg p-3 border-l-4 transition-all hover:bg-white/8" style={{ borderLeftColor: priorityColors[s.priority] || '#333' }} data-testid={`schedule-match-${i}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-white font-semibold">{s.teams}</span>
                                                <span className="text-[10px] px-2 py-0.5 rounded font-mono" style={{ background: `${priorityColors[s.priority]}20`, color: priorityColors[s.priority], border: `1px solid ${priorityColors[s.priority]}33` }}>
                                                    {s.priority.toUpperCase()}
                                                </span>
                                            </div>
                                            <span className="text-[10px] text-white/30 tech-mono">{s.hours_until > 0 ? `${s.hours_until}h away` : 'LIVE'}</span>
                                        </div>
                                        <div className="flex items-center gap-4 text-[10px] text-white/40">
                                            <span><Calendar className="w-3 h-3 inline mr-1" />{s.match_date?.slice(0, 10)}</span>
                                            <span><RefreshCw className="w-3 h-3 inline mr-1" />{s.recommended_frequency}</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {s.recommended_sources.map(src => (
                                                <span key={src} className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/30 border border-white/10">{src}</span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-white/20">
                                <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                <div className="text-xs">No upcoming matches found</div>
                            </div>
                        )}
                    </div>

                    {/* Budget Estimation */}
                    <div className="glass-card p-5" data-testid="budget-estimation">
                        <h3 className="text-xs text-white/40 tech-mono mb-3">ESTIMATED MONTHLY BUDGET (STARTER PLAN)</h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="border-b border-white/10">
                                        <th className="text-left py-2 px-2 text-white/30">SOURCE</th>
                                        <th className="text-right py-2 px-2 text-white/30">FREQUENCY</th>
                                        <th className="text-right py-2 px-2 text-white/30">~CU/RUN</th>
                                        <th className="text-right py-2 px-2 text-white/30">~$/MONTH</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        { src: 'Google Trends', freq: '2x daily', cu: '0.01', cost: '$0.18' },
                                        { src: 'Twitter/X', freq: 'Per match', cu: '0.5', cost: '$7.00' },
                                        { src: 'Reddit', freq: 'Per match', cu: '0.3', cost: '$4.20' },
                                        { src: 'News Scraper', freq: '1x daily', cu: '0.3', cost: '$2.70' },
                                        { src: 'YouTube', freq: '2x weekly', cu: '0.3', cost: '$2.40' },
                                        { src: 'Instagram', freq: '1x weekly', cu: '0.5', cost: '$2.00' },
                                        { src: 'Facebook', freq: '1x weekly', cu: '0.3', cost: '$1.20' },
                                    ].map((r, i) => (
                                        <tr key={i} className="border-b border-white/5">
                                            <td className="py-2 px-2 text-white/60">{r.src}</td>
                                            <td className="py-2 px-2 text-right text-white/40">{r.freq}</td>
                                            <td className="py-2 px-2 text-right text-amber-400">{r.cu}</td>
                                            <td className="py-2 px-2 text-right text-green-400">{r.cost}</td>
                                        </tr>
                                    ))}
                                    <tr className="border-t border-white/20">
                                        <td className="py-2 px-2 text-white font-bold" colSpan={3}>TOTAL</td>
                                        <td className="py-2 px-2 text-right text-green-400 font-bold">~$19.68</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="text-[10px] text-white/20 mt-2">Starter plan includes $29 credits. Well within budget with room for burst scraping on match days.</div>
                    </div>
                </div>
            )}

            {/* ─── RAW DATA TAB ────────────────────────────────────────── */}
            {tab === 'items' && (
                <div className="space-y-4">
                    <div className="glass-card p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <MessageSquare className="w-4 h-4 text-purple-400" />
                                <h3 className="text-sm text-white font-semibold">RAW INTELLIGENCE ITEMS</h3>
                                <span className="text-[10px] text-white/20 tech-mono">{items.length} items</span>
                            </div>
                        </div>

                        {items.length > 0 ? (
                            <div className="space-y-1 max-h-[600px] overflow-y-auto">
                                {items.map((item, i) => (
                                    <div key={i} className="bg-white/5 rounded-lg p-2.5 hover:bg-white/8 transition-all" data-testid={`item-${i}`}>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 tech-mono">{item.source}</span>
                                            <SentimentBadge label={item.sentiment_label} score={item.sentiment_score} />
                                            {item.author && <span className="text-[9px] text-white/20">@{item.author}</span>}
                                            <span className="text-[9px] text-white/10 ml-auto">{item.stored_at?.slice(0, 16)}</span>
                                        </div>
                                        <div className="text-xs text-white/50 line-clamp-2">{item.text}</div>
                                        {item.engagement > 0 && <div className="text-[9px] text-white/15 mt-1"><Activity className="w-3 h-3 inline mr-1" />{item.engagement} engagement</div>}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-white/20">
                                <Database className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                <div className="text-xs">No items stored yet</div>
                                <div className="text-[10px] mt-1">Trigger a scrape from Overview to start collecting data</div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
